import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL } from "../config";

const AuthContext = createContext(null);

const apiBase = () =>
  API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;

export const authStartUrl = () => `${apiBase()}/api/auth/start`;

export const startLogin = () => {
  window.location.assign(authStartUrl());
};

// Must be the Connect host the backend sends logins to (CONNECT_URL): the SSO
// cookie lives on that host's registrable domain.
const CONNECT_URL = (process.env.REACT_APP_CONNECT_URL || "https://connect.globe-data.tech").replace(/\/+$/, "");
const CONNECT_CLIENT_ID = process.env.REACT_APP_CONNECT_CLIENT_ID || "sellout-processor";

// Same registrable domain (heuristic: last two DNS labels). connectSid is
// SameSite=Lax, so a cross-site fetch would never carry it.
const isSameSite = (a, b) => {
  const tail = (host) => host.split(".").slice(-2).join(".");
  try {
    return tail(new URL(a).hostname) === tail(new URL(b).hostname);
  } catch {
    return false;
  }
};

// Ends the SSO session. Only the browser holds connectSid, so the call cannot go
// through the backend. Best-effort: the session expires on its own otherwise.
const endConnectSession = async () => {
  if (!isSameSite(CONNECT_URL, window.location.href)) return;
  try {
    await fetch(`${CONNECT_URL}/oauth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: CONNECT_CLIENT_ID }),
    });
  } catch {
    // Network error: nothing more to do from here.
  }
};

const AUTH_ERROR_TEXT = {
  pkce: "La session de connexion a expiré. Réessayez.",
  state: "La vérification de connexion a échoué. Réessayez.",
  exchange: "Connect n'a pas pu terminer la connexion.",
  denied: "Connexion refusée.",
  unavailable: "Le service d'authentification est indisponible.",
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [logoutError, setLogoutError] = useState(null);
  // A 401 from another call in flight must not bounce through /auth/start while
  // the logout is still revoking the session.
  const loggingOut = useRef(false);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!url.startsWith("http")) {
      const baseUrl = apiBase();
      url = `${baseUrl}/${url.startsWith("/") ? url.substring(1) : url}`;
    }

    const response = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        ...options.headers,
      },
    });

    if (response.status === 401 && !loggingOut.current) {
      window.location.assign(authStartUrl());
      return response;
    }

    if (response.status === 403) {
      const data = await response.json().catch(() => ({}));
      if (data.errorType === "REPORT_ACCESS_DENIED") {
        return {
          ok: false,
          status: 403,
          json: () => Promise.resolve(data),
        };
      }
    }

    return response;
  }, []);

  const logout = useCallback(async () => {
    setLogoutError(null);
    loggingOut.current = true;
    let response;
    try {
      response = await fetch(`${apiBase()}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      loggingOut.current = false;
      setLogoutError("La déconnexion n'a pas abouti. Vous êtes toujours connecté.");
      return;
    }
    if (response.status === 503 || !response.ok) {
      loggingOut.current = false;
      const data = await response.json().catch(() => ({}));
      setLogoutError(
        data.detail || "La déconnexion n'a pas abouti. Vous êtes toujours connecté."
      );
      return;
    }
    // With the SSO session gone, Connect answers with its login form instead of
    // signing the same account straight back in.
    await endConnectSession();
    startLogin();
  }, []);

  // A logout in another Globe app ends the shared session, but this tab only learns
  // it on its next request. Re-check when the user comes back to the tab.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onVisible = async () => {
      if (document.hidden || loggingOut.current) return;
      try {
        const response = await fetch(`${apiBase()}/api/me`, { credentials: "include" });
        if (response.status === 401) startLogin();
      } catch {
        // Network error: the next return to the tab retries.
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isAuthenticated]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("auth_error");
    if (code) {
      setAuthError(AUTH_ERROR_TEXT[code] || AUTH_ERROR_TEXT.exchange);
      setIsAuthenticated(false);
      setIsCheckingAuth(false);
      return;
    }

    let cancelled = false;
    let redirecting = false;

    const checkAuth = async () => {
      try {
        const response = await fetch(`${apiBase()}/api/me`, {
          credentials: "include",
        });
        if (response.status === 401) {
          redirecting = true;
          window.location.assign(authStartUrl());
          return;
        }
        if (!response.ok) {
          throw new Error("Authentication failed");
        }
        const userData = await response.json();
        if (cancelled) return;
        setCurrentUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Auth check failed:", error);
        if (!cancelled) {
          setAuthError(AUTH_ERROR_TEXT.unavailable);
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled && !redirecting) {
          setIsCheckingAuth(false);
        }
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticating: isCheckingAuth,
        isAuthenticated,
        currentUser,
        authError,
        logoutError,
        fetchWithAuth,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
